import { useInfiniteQuery } from 'react-query';
import { useState, useCallback, useMemo } from 'react';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { useWorkspaceContext } from 'hooks/common';
import {
  FilterParameters,
  generateFilter,
  generateFilterExpression,
  generateIncludeQuery,
  generatePaginationQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';

import type { Plugins, PluginsResponse, PluginFunctions } from 'types/Plugins';
import type { PluginConnections } from 'types/PluginConnections';
import type { PluginAuthentication } from 'types/PluginAuthentications';

export type PluginSearchResult = {
  plugin: Plugins;
  pluginFunctions: PluginFunctions[];
  pluginConnections: PluginConnections[];
  pluginAuthentications: PluginAuthentication[];
};

const getPluginsWithDetails = async (
  pageParam: number,
  queryString: string | null | undefined,
  pageSize: number = 5
) => {
  const filters: FilterParameters[] = [
    {
      key: 'pluginPrivacyTypeID',
      value: 1,
      type: 'equal',
    },
  ];

  if (queryString && queryString.length >= 3) {
    filters.push({
      key: 'name',
      value: queryString,
      type: 'contains',
    });
  }

  const queryArray = [
    filters.length > 1
      ? generateFilterExpression('and', filters)
      : generateFilter('pluginPrivacyTypeID', 1, 'equal'),
    generateIncludeQuery([
      'pluginConnections',
      'pluginAuthentications',
      'pluginFunctions',
    ]),
    generatePaginationQuery({
      pageNumber: pageParam,
      pageSize: pageSize,
    }),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.PLUGINS, queryArray);

  return fetchWrapper<PluginsResponse>({
    url,
  });
};

export const useInfinitePluginSearch = (initialQuery: string = '') => {
  const [searchQuery, setSearchQuery] = useState<string>(initialQuery);

  const {
    data: pluginResponse,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    ...rest
  } = useInfiniteQuery({
    queryKey: ['plugins-search', searchQuery],
    queryFn: ({ pageParam = 1 }) =>
      getPluginsWithDetails(pageParam, searchQuery),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.data.length ? allPages.length + 1 : undefined;
    },
    enabled: searchQuery.length === 0 || searchQuery.length >= 3,
    // Ensure the query runs immediately on mount
    staleTime: 0,
  });

  const plugins: PluginSearchResult[] = useMemo(() => {
    if (!pluginResponse?.pages) return [];

    return pluginResponse.pages.reduce<PluginSearchResult[]>((acc, page) => {
      if (!page.data) {
        return acc;
      }

      const pluginFunctions = (page.included || []).filter(
        (item) => item.type === 'pluginFunctions'
      ) as PluginFunctions[];

      const connections = filterIncludedItem<PluginConnections>(
        page.included,
        SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS
      );

      const pluginAuthentications = filterIncludedItem<PluginAuthentication>(
        page.included,
        SPARK_RESOURCE_TYPES.PLUGIN_AUTHENTICATION
      );

      const pluginsWithDetails = page.data.map((plugin) => {
        const pluginID = plugin.id;

        const filteredFunctions =
          pluginFunctions?.filter(
            (func) => func.attributes.pluginID === pluginID
          ) || [];

        const filteredConnections =
          connections?.filter(
            (connection) => connection.attributes.pluginID === pluginID
          ) || [];

        const filteredAuthentications =
          pluginAuthentications?.filter(
            (auth) => auth.attributes.pluginID === pluginID
          ) || [];

        return {
          plugin,
          pluginFunctions: filteredFunctions,
          pluginConnections: filteredConnections,
          pluginAuthentications: filteredAuthentications,
        };
      });

      return [...acc, ...pluginsWithDetails];
    }, []);
  }, [pluginResponse?.pages]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  return {
    plugins,
    searchQuery,
    handleSearch,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    ...rest,
  };
};
